/**
 * GEN-PX-034 · Cálculos de la página de un área de Saber, todos a partir del `mapa` de GET /v1/ekb/saber (sin DDL ni llamadas nuevas).
 * Una subárea es un área con `padre_id`; sus KBs directos vienen en su tarjeta del mapa.
 */
const DIA = 86400000
export const DIAS_AL_DIA = 30
export const DIAS_CADUCADO = 90

export const normalizarNombre = n => String(n ?? '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, ' ').trim()

function distancia(a, b) {
  let previa = Array.from({ length: b.length + 1 }, (_, j) => j)
  for (let i = 1; i <= a.length; i++) {
    const fila = [i]
    for (let j = 1; j <= b.length; j++) fila[j] = Math.min(previa[j] + 1, fila[j - 1] + 1, previa[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1))
    previa = fila
  }
  return previa[b.length]
}

/** «Casi igual»: mismo nombre normalizado (minúsculas, sin tildes ni puntuación) o muy parecido (≥ 85 % de letras comunes y las mismas cifras). Sin modelo. */
export function nombresParecidos(a, b) {
  const x = normalizarNombre(a), y = normalizarNombre(b)
  if (!x || !y) return false
  if (x === y || x.replace(/ /g, '') === y.replace(/ /g, '')) return true
  if (Math.min(x.length, y.length) < 8) return false
  const cifras = s => (s.match(/\d+/g) ?? []).join(',')
  if (cifras(x) !== cifras(y)) return false
  return 1 - distancia(x, y) / Math.max(x.length, y.length) >= 0.85
}

const hijas = (mapa, id) => mapa.filter(a => a.padre_id === id)

/** Áreas de la rama: el área y todas sus descendientes. */
export function ramaDe(area, mapa) {
  const salida = [area]
  const vistos = new Set([area.id])
  for (let i = 0; i < salida.length; i++) {
    for (const h of hijas(mapa, salida[i].id)) if (!vistos.has(h.id)) { vistos.add(h.id); salida.push(h) }
  }
  return salida
}

/** KBs de la rama sin repetir (un KB «también en» otra área cuenta una vez; manda su ficha de área principal). */
export function kbsDeRama(area, mapa) {
  const por = new Map()
  for (const a of ramaDe(area, mapa)) for (const k of a.kbs ?? []) if (!por.has(k.id) || por.get(k.id).tambien_en) por.set(k.id, k)
  return [...por.values()]
}

export const ultimoCambio = kbs => kbs.map(k => k.actualizado).filter(Boolean).sort().at(-1) ?? null

export function nivelFrescura(iso, ahora = Date.now()) {
  const t = Date.parse(iso ?? '')
  if (Number.isNaN(t)) return 'gris'
  const dias = Math.max(0, Math.floor((ahora - t) / DIA))
  return dias < DIAS_AL_DIA ? 'verde' : dias <= DIAS_CADUCADO ? 'ambar' : 'gris'
}

/** Reparto de frescura de unos KBs: { verde, ambar, gris, total } (al día · 1-3 meses · más de 3 meses). */
export function repartoFrescura(kbs, ahora = Date.now()) {
  const r = { verde: 0, ambar: 0, gris: 0, total: kbs.length }
  for (const k of kbs) r[nivelFrescura(k.actualizado, ahora)]++
  return r
}

/** Los n cambios más recientes del área: KB y subárea directa donde está. */
export function ultimosCambios(area, mapa, n = 5) {
  const por = new Map()
  const mirar = (a, sub) => {
    for (const k of a.kbs ?? []) if (k.actualizado && (!por.has(k.id) || (por.get(k.id).kb.tambien_en && !k.tambien_en))) por.set(k.id, { kb: k, sub })
  }
  mirar(area, null)
  for (const s of hijas(mapa, area.id)) for (const a of ramaDe(s, mapa)) mirar(a, s)
  return [...por.values()].sort((x, y) => y.kb.actualizado.localeCompare(x.kb.actualizado)).slice(0, n)
}

/**
 * Avisos para el responsable de un área. Devuelve:
 *  - `resumen`: [{ clave, n, texto }] (la franja);
 *  - `porKb`: Map id de KB → badges [{ clave, texto }];
 *  - `porSub`: Map id de subárea → badges (propios, más `n` = avisos de ella y de sus KBs).
 * «KBs sin subárea» solo cuenta si el área tiene subáreas (sin ellas, todos serían «sin subárea»).
 */
export function avisosDeArea(area, mapa, ahora = Date.now()) {
  const subs = hijas(mapa, area.id)
  const porKb = new Map()
  const porSub = new Map()
  const poner = (m, id, b) => m.set(id, [...(m.get(id) ?? []), b])
  const kbs = kbsDeRama(area, mapa)
  const sueltos = (area.kbs ?? []).filter(k => !k.tambien_en)

  if (subs.length) for (const k of sueltos) poner(porKb, k.id, { clave: 'sinSubarea', texto: 'Sin subárea' })
  for (const k of kbs) if (!k.ikus) poner(porKb, k.id, { clave: 'vacio', texto: 'Vacío' })
  const parejas = new Set()
  for (let i = 0; i < kbs.length; i++) {
    for (let j = i + 1; j < kbs.length; j++) {
      if (!nombresParecidos(kbs[i].nombre, kbs[j].nombre)) continue
      parejas.add(kbs[i].id).add(kbs[j].id)
      poner(porKb, kbs[i].id, { clave: 'parecido', texto: `Nombre casi igual a «${kbs[j].nombre}»` })
      poner(porKb, kbs[j].id, { clave: 'parecido', texto: `Nombre casi igual a «${kbs[i].nombre}»` })
    }
  }
  let sinKbs = 0, sinCambios = 0
  for (const s of subs) {
    const ks = kbsDeRama(s, mapa)
    if (!ks.length) { sinKbs++; poner(porSub, s.id, { clave: 'subSinKbs', texto: 'Sin KBs' }) } else {
      const u = ultimoCambio(ks)
      if (nivelFrescura(u, ahora) === 'gris') { sinCambios++; poner(porSub, s.id, { clave: 'subSinCambios', texto: 'Más de 3 meses sin cambios' }) }
    }
  }
  const n = clave => [...porKb.values()].filter(bs => bs.some(b => b.clave === clave)).length
  const resumen = [
    { clave: 'sinSubarea', n: subs.length ? sueltos.length : 0, uno: 'KB sin subárea', varios: 'KBs sin subárea' },
    { clave: 'vacio', n: n('vacio'), uno: 'KB vacío', varios: 'KBs vacíos' },
    { clave: 'parecido', n: parejas.size, uno: 'KB con nombre casi igual', varios: 'KBs con nombre casi igual' },
    { clave: 'subSinKbs', n: sinKbs, uno: 'subárea sin KBs', varios: 'subáreas sin KBs' },
    { clave: 'subSinCambios', n: sinCambios, uno: 'subárea sin cambios en más de 3 meses', varios: 'subáreas sin cambios en más de 3 meses' },
  ].filter(x => x.n > 0).map(x => ({ clave: x.clave, n: x.n, texto: `${x.n} ${x.n === 1 ? x.uno : x.varios}` }))
  // avisos por subárea = los suyos + los de los KBs de su rama (para ordenar y para el contador de la fila)
  const total = {}
  for (const s of subs) total[s.id] = (porSub.get(s.id)?.length ?? 0) + kbsDeRama(s, mapa).reduce((t, k) => t + (porKb.get(k.id)?.length ?? 0), 0)
  return { resumen, porKb, porSub, totalSub: total, hay: resumen.length > 0 }
}

/** ¿Ve los avisos? El administrador, o quien es titular de algún KB del área (rama incluida). */
export function puedeVerAvisos(area, mapa, rol, email) {
  if (rol === 'admin') return true
  if (!email) return false
  return kbsDeRama(area, mapa).some(k => k.titular?.email === email)
}
